import type { PrimitiveDef, SubstrateManifest } from '../manifest';
import { orderedNodes } from './defaultGraph';
import type {
  ApprovalDecision,
  AssetRecord,
  BenchGraph,
  BenchNode,
  CompositionRunResult,
  PublishedRecord,
  RunNodeState,
} from './types';

function nowIso(): string {
  return new Date().toISOString();
}

function makeId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function primitiveById(manifest: SubstrateManifest, primitiveId: string): PrimitiveDef {
  const primitive = manifest.primitives.find((candidate) => candidate.id === primitiveId);
  if (!primitive) throw new Error(`Unknown primitive in composition run: ${primitiveId}`);
  return primitive;
}

function appendAsset(
  assets: AssetRecord[],
  runId: string,
  node: BenchNode,
  type: string,
  label: string,
  payload: Record<string, unknown>
): Record<string, unknown> {
  const asset: AssetRecord = {
    id: makeId(type),
    type,
    label,
    runId,
    primitiveId: node.primitiveId,
    createdAt: nowIso(),
    payload,
  };
  assets.push(asset);
  return payload;
}

function executePrimitive(
  node: BenchNode,
  input: Record<string, unknown>,
  runId: string,
  assets: AssetRecord[]
): Record<string, unknown> {
  switch (node.primitiveId) {
    case 'source.ingest':
      return appendAsset(assets, runId, node, 'document_stream', 'Document Stream', {
        source_type: node.parameters.source_type ?? 'url',
        locator: node.parameters.locator ?? 'https://example.local/source',
        documents: [
          {
            id: 'doc-1',
            text: 'A normalized source document produced by the Seitiate+ substrate composer.',
          },
        ],
      });
    case 'codex.chunk_get':
      return appendAsset(assets, runId, node, 'codex_chunk_set', 'Codex Chunk Set', {
        query: input.query ?? 'substrate workbench',
        chunks: [
          {
            id: 'chunk-1',
            text: 'Codex context retrieved for the composed substrate run.',
            source: 'document_stream',
          },
        ],
      });
    case 'llm.complete':
      return appendAsset(assets, runId, node, 'completion_text', 'Completion Text', {
        model: node.parameters.model || 'local-v1-preview',
        temperature: node.parameters.temperature ?? 0.7,
        completion:
          'Draft artifact generated from source context and Codex chunks. Awaiting approval before publication.',
      });
    case 'approval.gate':
      return {
        review_payload: input,
        approval_status: 'pending',
      };
    case 'sink.publish':
      return appendAsset(assets, runId, node, 'publication_receipt', 'Publication Receipt', {
        sink_type: node.parameters.sink_type ?? 'webhook',
        destination: node.parameters.destination ?? 'local-preview',
        status: 'published',
        artifact: input,
      });
    default:
      return {
        primitive_id: node.primitiveId,
        output: input,
      };
  }
}

export function runSubstrateComposition({
  manifest,
  graph,
  approvalDecision,
}: {
  manifest: SubstrateManifest;
  graph: BenchGraph;
  approvalDecision?: 'approve' | 'reject' | 'modify';
}): CompositionRunResult {
  const runId = makeId('run');
  const startedAt = nowIso();
  const nodes = orderedNodes(graph);
  const nodeStates: RunNodeState[] = nodes.map((node) => ({
    nodeId: node.id,
    primitiveId: node.primitiveId,
    label: node.label,
    status: 'idle',
  }));
  const assets: AssetRecord[] = [];
  const decisions: ApprovalDecision[] = [];
  const outputByNodeId: Record<string, Record<string, unknown>> = {};
  let currentPayload: Record<string, unknown> = {};

  for (const node of nodes) {
    const state = nodeStates.find((candidate) => candidate.nodeId === node.id);
    if (!state) continue;
    primitiveById(manifest, node.primitiveId);
    state.status = 'running';
    state.startedAt = nowIso();

    if (node.primitiveId === 'approval.gate' && !approvalDecision) {
      state.status = 'paused';
      state.finishedAt = nowIso();
      currentPayload = executePrimitive(node, currentPayload, runId, assets);
      outputByNodeId[node.id] = currentPayload;
      return {
        run: {
          id: runId,
          graphId: graph.id,
          graphLabel: graph.label,
          startedAt,
          finishedAt: null,
          status: 'paused',
          pausedAtNodeId: node.id,
          nodes: nodeStates,
          decisions,
          assetDeltas: assets,
        },
        assets,
        published: [],
        outputByNodeId,
      };
    }

    if (node.primitiveId === 'approval.gate' && approvalDecision) {
      const before = currentPayload;
      const after =
        approvalDecision === 'modify'
          ? { ...before, approval_note: 'Modified in Plus Workbench V1 local approval gate.' }
          : before;
      decisions.push({
        nodeId: node.id,
        primitiveId: node.primitiveId,
        action: approvalDecision === 'reject' ? 'rejected' : approvalDecision === 'modify' ? 'modified' : 'approved',
        at: nowIso(),
        payloadBefore: before,
        payloadAfter: after,
      });
      if (approvalDecision === 'reject') {
        state.status = 'rejected';
        state.finishedAt = nowIso();
        return {
          run: {
            id: runId,
            graphId: graph.id,
            graphLabel: graph.label,
            startedAt,
            finishedAt: nowIso(),
            status: 'rejected',
            nodes: nodeStates,
            decisions,
            assetDeltas: assets,
          },
          assets,
          published: [],
          outputByNodeId,
        };
      }
      currentPayload = after;
      outputByNodeId[node.id] = currentPayload;
    } else {
      currentPayload = executePrimitive(node, currentPayload, runId, assets);
      outputByNodeId[node.id] = currentPayload;
    }

    state.status = 'completed';
    state.finishedAt = nowIso();
  }

  const published: PublishedRecord[] = assets
    .filter((asset) => asset.type === 'publication_receipt')
    .map((asset) => ({
      id: makeId('published'),
      runId,
      sinkType: String(asset.payload.sink_type ?? 'webhook'),
      destination: String(asset.payload.destination ?? 'local-preview'),
      status: 'published',
      createdAt: nowIso(),
      receipt: asset.payload,
    }));

  return {
    run: {
      id: runId,
      graphId: graph.id,
      graphLabel: graph.label,
      startedAt,
      finishedAt: nowIso(),
      status: 'completed',
      nodes: nodeStates,
      decisions,
      assetDeltas: assets,
    },
    assets,
    published,
    outputByNodeId,
  };
}
